import { Injectable } from '@angular/core';
import {
  ConditionalActionFlowExecuter,
  DynamicConditionalActionFlowBuilder,
  SelectNextByActionStepBuilder,
  SelectNextByValueStepBuilder,
  StepNextType,
} from '@epicuro-next/state/dynamic-conditional-action-flow';
import { modalActionGroup } from '@epicuro-next/state/modal';
import { go } from '@epicuro-next/state/router-store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';
import { Action, Store } from '@ngrx/store';
import { concatMap, Observable, switchMap } from 'rxjs';

import {
  confirmToPersistBeforeLeaving,
  leavingAborted,
  persistChanges,
  persistChangesFailure,
  persistChangesSuccess,
} from '../actions';
import { selectCanPersist } from '../selectors';

const CONFIRM_LEAVE_TITLE = $localize`:ConfigStrip|Confirm leaving config strip title@@confirmLeavingConfigStripTitle:If you proceed without adding products to basket the entered quantities will be lost.`;
const CONFIRM_LEAVE_MESSAGE = $localize`:ConfigStrip|Confirm leaving config strip message@@confirmLeavingConfigStripMessage:Choose one of the options below.`;
const CONFIRM_LEAVE_ANSWER_CANCEL = $localize`:ConfigStrip|Confirm leaving config strip answer cancel@@confirmLeavingConfigStripAnswerCancel:Cancel`;
const CONFIRM_LEAVE_ANSWER_CONTINUE_WITHOUT_PERSISTING = $localize`:ConfigStrip|Confirm leaving config strip answer continue without persisting@@confirmLeavingConfigStripAnswerContinueWithoutPersisting:Go`;
const CONFIRM_LEAVE_ANSWER_CONTINUE_WITH_PERSISTING = $localize`:ConfigStrip|Confirm leaving config strip answer continue with persisting@@confirmLeavingConfigStripAnswerContinueWithPersisting:Add to basket and go`;

@Injectable()
export class ConfigStripDeactivationEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
  ) {}

  public persistBeforeLeavingConfirmation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(confirmToPersistBeforeLeaving),
      switchMap((action) => {
        return this.AskUserToAddToBasketFirstBeforeLeaving(
          action.navigationAction,
          action.continuationActions,
        ).pipe(concatMap((actions) => actions));
      }),
    ),
  );

  private AskUserToAddToBasketFirstBeforeLeaving(
    navigationAction: ReturnType<typeof go> | undefined,
    continuationActions: Action[],
  ): Observable<Action[]> {
    let nextStep: StepNextType;
    if (navigationAction !== undefined) {
      const navigationFlow = new DynamicConditionalActionFlowBuilder(
        'Navigate to search and wait for navigation to end before continuing',
      )
        .addStep(
          new SelectNextByActionStepBuilder()
            .dispatch(navigationAction)
            .addActionToStep(
              (action) => action.type === ROUTER_NAVIGATED,
              continuationActions,
            ),
        )
        .build();

      nextStep = navigationFlow;
    } else {
      nextStep = continuationActions;
    }

    const persistAndContinueStep = new DynamicConditionalActionFlowBuilder(
      'Persist config-strip and goto p-basket',
    )
      .addStep(
        new SelectNextByActionStepBuilder()
          .dispatch(persistChanges())
          .addActionsToSteps([
            [persistChangesFailure, leavingAborted()],
            [persistChangesSuccess(), nextStep],
          ]),
      )
      .build();

    const answerValues = {
      none: null,
      continueWithoutPersisting: 'continue-without-persisting',
      continueWithPersisting: 'continue-with-persisting',
    };

    // prettier-ignore
    const askWhatToDoFlow = new DynamicConditionalActionFlowBuilder(
      'Ask user how to continue',
    )
      .addStep(
        new SelectNextByActionStepBuilder()
          .dispatch(
            modalActionGroup.show({
              choices: [
                {
                  label: CONFIRM_LEAVE_ANSWER_CANCEL,
                  value: answerValues.none,
                },
                {
                  label: CONFIRM_LEAVE_ANSWER_CONTINUE_WITHOUT_PERSISTING,
                  value: answerValues.continueWithoutPersisting,
                },
                {
                  label: CONFIRM_LEAVE_ANSWER_CONTINUE_WITH_PERSISTING,
                  value: answerValues.continueWithPersisting,
                  isPrimary: true,
                },
              ],
              message: CONFIRM_LEAVE_MESSAGE,
              noAnswerValue: answerValues.none,
              title: CONFIRM_LEAVE_TITLE,
            }),
          )
          .addActionsToSteps([
            [modalActionGroup.answer({ answer: answerValues.none }), leavingAborted()],
            [modalActionGroup.answer({ answer: answerValues.continueWithoutPersisting }), nextStep],
            [modalActionGroup.answer({ answer: answerValues.continueWithPersisting }), persistAndContinueStep],
            [modalActionGroup.answer, leavingAborted()],
          ]),
      )
      .build();

    const configStripCanPersistChanges$ = this.store.select(selectCanPersist);

    const configStripCanPersistChangesStep = new SelectNextByValueStepBuilder(
      configStripCanPersistChanges$,
    ).addValuesToSteps([
      [false, nextStep],
      [(v) => v === true, askWhatToDoFlow],
    ]);

    const flow = new DynamicConditionalActionFlowBuilder(
      'Optionally persist changes for config-strip and continue',
    )
      .addStep(configStripCanPersistChangesStep)
      .build();

    return ConditionalActionFlowExecuter(flow, this.store, this.actions$);
  }
}
